import { ReactNode } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogOverlay, DialogTitle } from '@goodday-clone/ui';
import { Button } from './Button';

interface ConfirmDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  /** Extra controls below the message, e.g. the reason an action needs. */
  children?: ReactNode;
}

// Built on the same dialog primitive as the app's other modals: a hand-rolled portal
// would sit outside the pointer-events lock an open modal puts on <body>, which leaves
// this dialog visible but unable to be clicked.

export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  loading = false,
  children,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !loading) onCancel();
      }}
    >
      <DialogOverlay className="z-[70] bg-black/50" />
      <DialogContent
        hideCloseButton
        role="alertdialog"
        className="z-[70] max-w-md gap-0 rounded-xl border-0 bg-white p-0 shadow-xl dark:bg-gray-900"
      >
        <div className="px-5 py-4">
          <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {message}
          </DialogDescription>
          {children && <div className="mt-3">{children}</div>}
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-3 dark:border-gray-700">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'destructive' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            disabled={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
